import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Apple, Download, X } from 'lucide-react';
import { APP_STORE_URL } from '@/lib/appStoreUrl';

/**
 * Reusable "Choose your platform" download modal.
 *
 * Props:
 *   - open: boolean — controls visibility
 *   - onClose: () => void
 *   - title: string — header text (defaults to the iPhone/Android question)
 *   - subtitle: string — secondary line under the title
 *   - footer: string — small reassurance line under the tiles
 */
export default function DownloadModal({
  open,
  onClose,
  title = 'Vous avez un iPhone ou un Android\u00A0?',
  subtitle = 'Choisissez votre appareil pour installer ClayPulse.',
  footer = "7 jours d'essai gratuit \u00B7 Sans CB \u00B7 8 disciplines",
}) {
  // Auto-detect OS so we can flag the recommended tile.
  const [detectedOS, setDetectedOS] = useState(null); // 'ios' | 'android' | null
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) setDetectedOS('ios');
    else if (/Android/i.test(ua)) setDetectedOS('android');
  }, []);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="dl-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="download-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 12 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#0D0D0D] border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.8)] p-7 sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dl-title"
            data-testid="download-modal"
          >
            <button
              onClick={onClose}
              aria-label="Fermer"
              data-testid="download-modal-close"
              className="absolute top-4 right-4 p-2 rounded-full text-white/50 hover:text-white hover:bg-white/5 transition"
            >
              <X size={18} />
            </button>

            <div className="text-center mb-7">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-4">
                <Download size={24} className="text-[#FF6B35]" />
              </div>
              <h3 id="dl-title" className="text-white text-xl sm:text-2xl font-bold font-['Poppins'] mb-2">
                {title}
              </h3>
              <p className="text-[#A0A0A0] text-sm">{subtitle}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* iOS tile — goes straight to the App Store */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-modal-ios"
                className={`relative group flex flex-col items-center justify-center gap-2 p-5 rounded-2xl border transition-all duration-300 hover:scale-[1.03] ${
                  detectedOS === 'ios'
                    ? 'bg-[#FF6B35]/10 border-[#FF6B35]/50'
                    : 'bg-white/[0.03] border-white/10 hover:border-[#FF6B35]/40 hover:bg-white/[0.05]'
                }`}
              >
                {detectedOS === 'ios' && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#FF6B35] text-white text-[9px] font-bold uppercase tracking-wider">
                    Recommandé
                  </span>
                )}
                <Apple size={32} className="text-white group-hover:text-[#FF6B35] transition-colors" />
                <div className="text-center">
                  <div className="text-white font-semibold text-sm">iPhone</div>
                  <div className="text-[#888] text-[11px] mt-0.5">Télécharger sur l&apos;App Store</div>
                </div>
              </a>

              {/* Android tile */}
              <a
                href="/android"
                data-testid="download-modal-android"
                className={`relative group flex flex-col items-center justify-center gap-2 p-5 rounded-2xl border transition-all duration-300 hover:scale-[1.03] ${
                  detectedOS === 'android'
                    ? 'bg-[#FF6B35]/10 border-[#FF6B35]/50'
                    : 'bg-white/[0.03] border-white/10 hover:border-[#FF6B35]/40 hover:bg-white/[0.05]'
                }`}
              >
                {detectedOS === 'android' && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#FF6B35] text-white text-[9px] font-bold uppercase tracking-wider">
                    Recommandé
                  </span>
                )}
                <svg viewBox="0 0 24 24" width="32" height="32" className="text-white group-hover:text-[#FF6B35] transition-colors" fill="currentColor" aria-hidden="true">
                  <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zM15.53 2.16l1.3-1.3a.36.36 0 0 0-.5-.5l-1.48 1.48C14.16 1.55 13.13 1.3 12 1.3s-2.16.25-3.06.55L7.46.37a.36.36 0 0 0-.5.5l1.3 1.29C6.49 3.04 5.04 4.85 5.04 7H19c0-2.15-1.45-3.96-3.47-4.84zM9 5.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm6 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1z" />
                </svg>
                <div className="text-center">
                  <div className="text-white font-semibold text-sm">Android</div>
                  <div className="text-[#888] text-[11px] mt-0.5">APK v1.0.39</div>
                </div>
              </a>
            </div>

            {footer && (
              <p className="mt-5 text-center text-[#666] text-[11px]">{footer}</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
